/**
 * HoursFields — the ERA 1996 s.8(2)(e) surfaces: the read-only itemisation
 * both previews render, and the REQUIRED either/or section the record and
 * correction gates embed for a time-varying worker.
 *
 * Split from hours.ts only because react-refresh requires a module to export
 * components OR values, not both. The conversion reasoning lives there.
 */
import { Plus, Trash2 } from 'lucide-react'
import type { PayrollHours } from '../../types'
import {
  centiToHoursText, conversionEcho, emptyHoursDraft, hoursDraftComplete,
  totalCenti, type HoursAnswer, type HoursDraft,
} from './hours'

const rowInputCls = 'h-9 px-2 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent'

/** The read-only itemisation. An empty list is a SENTENCE, not blank space:
 *  for a time-varying worker "no variable hours this period" is a real
 *  answer, and the caller only renders this at all when the duty attaches. */
export function HoursList({ hours }: { hours: PayrollHours[] }) {
  if (hours.length === 0) {
    return (
      <div className="text-13 text-ink-secondary leading-relaxed">
        No variable hours this period.
      </div>
    )
  }
  const total = hours.reduce((s, h) => s + h.hoursCenti, 0)
  return (
    <div className="flex flex-col gap-1">
      <ul className="flex flex-col gap-1">
        {hours.map((h, i) => (
          <li key={`${h.workType ?? ''}-${h.ratePencePerHour ?? ''}-${i}`} className="text-13 text-ink-body leading-relaxed">
            <span className="tabular-nums font-medium text-ink-primary">{centiToHoursText(h.hoursCenti)} h</span>
            {(h.workType || h.ratePencePerHour !== undefined) && (
              <span className="text-ink-muted">
                {' ('}
                {h.workType ?? ''}
                {h.workType && h.ratePencePerHour !== undefined ? ', ' : ''}
                {h.ratePencePerHour !== undefined ? `£${(h.ratePencePerHour / 100).toFixed(2)}/h` : ''}
                {')'}
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="text-12 text-ink-muted">
        Total <span className="tabular-nums font-medium text-ink-primary">{centiToHoursText(total)} h</span>
        {' '}({total} centi-hours)
      </div>
    </div>
  )
}

/**
 * The REQUIRED either/or. Rendered ONLY when the payday's declaration is
 * true — a flag-false worker sees no hours surface at all, because showing an
 * optional-looking section would invite supplying hours the server refuses by
 * name.
 *
 * NEITHER CHOICE IS PRESELECTED, for the same reason the profile form's radio
 * pair is not: defaulting to "no variable hours" would send an answer the
 * operator never gave. `answer` starts 'unanswered' and the caller keeps
 * submit shut until it moves.
 */
export function HoursSection({ idPrefix, answer, onAnswer, drafts, onChange, disabled = false }: {
  idPrefix: string
  answer: HoursAnswer
  onAnswer: (a: HoursAnswer) => void
  drafts: HoursDraft[]
  onChange: (next: HoursDraft[]) => void
  disabled?: boolean
}) {
  function patch(i: number, field: keyof HoursDraft, value: string) {
    onChange(drafts.map((d, j) => (j === i ? { ...d, [field]: value } : d)))
  }

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
          Hours worked <span className="text-danger">(required)</span>
        </div>
        <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
          This employee’s pay varies by reference to time worked, so the hours for the variable
          part of their pay must be recorded (ERA 1996 s.8(2)(e)). Give the hours, or say there
          were none this period — the two are different answers and one must be chosen.
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="flex items-start gap-2 text-13 text-ink-body">
          <input type="radio" name={`${idPrefix}-answer`} value="rows" disabled={disabled}
            checked={answer === 'rows'}
            onChange={() => { onAnswer('rows'); if (drafts.length === 0) onChange([emptyHoursDraft()]) }}
            className="mt-0.5" />
          <span>Record the hours worked</span>
        </label>
        <label className="flex items-start gap-2 text-13 text-ink-body">
          <input type="radio" name={`${idPrefix}-answer`} value="none" disabled={disabled}
            checked={answer === 'none'}
            onChange={() => onAnswer('none')}
            className="mt-0.5" />
          <span>
            No variable hours this period
            <span className="text-ink-muted"> — recorded as an explicit “none”, not left blank</span>
          </span>
        </label>
      </div>

      {answer === 'rows' && (
        <div className="flex flex-col gap-2 pl-6">
          {drafts.map((d, i) => (
            <div key={i} className="grid grid-cols-[8rem_1fr_8rem_2rem] gap-2 items-end">
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-amount-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">Hours</label>}
                <input id={`${idPrefix}-amount-${i}`} type="number" min={0} step={0.01} value={d.amount}
                  aria-label={`Hours row ${i + 1} amount`} disabled={disabled}
                  onChange={e => patch(i, 'amount', e.target.value)}
                  placeholder="7.5" className={`${rowInputCls} w-full text-right tabular-nums`} />
                {/* THE CONVERSION, ECHOED. The wire unit is hundredths, and
                    this is what stops 7.5 being mistaken for 7.5 centi. */}
                <div className="text-11 text-ink-muted mt-0.5 tabular-nums" data-testid={`${idPrefix}-echo-${i}`}>
                  {conversionEcho(d.amount)}
                </div>
              </div>
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-worktype-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">
                  Work type <span className="font-normal normal-case tracking-normal text-ink-muted">(optional)</span>
                </label>}
                <input id={`${idPrefix}-worktype-${i}`} value={d.workType} disabled={disabled}
                  aria-label={`Hours row ${i + 1} work type`}
                  onChange={e => patch(i, 'workType', e.target.value)}
                  placeholder="normal rate" className={`${rowInputCls} w-full`} />
              </div>
              <div>
                {i === 0 && <label htmlFor={`${idPrefix}-rate-0`} className="block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">
                  Rate £/h <span className="font-normal normal-case tracking-normal text-ink-muted">(optional)</span>
                </label>}
                <input id={`${idPrefix}-rate-${i}`} type="number" min={0} step={0.01} value={d.rate} disabled={disabled}
                  aria-label={`Hours row ${i + 1} rate`}
                  onChange={e => patch(i, 'rate', e.target.value)}
                  placeholder="12.50" className={`${rowInputCls} w-full text-right tabular-nums`} />
              </div>
              <button type="button" disabled={disabled}
                onClick={() => onChange(drafts.filter((_, j) => j !== i))}
                aria-label={`Remove hours row ${i + 1}`}
                className="h-9 w-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors disabled:opacity-40">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <button type="button" disabled={disabled}
              onClick={() => onChange([...drafts, emptyHoursDraft()])}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-11 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover disabled:opacity-40">
              <Plus size={12} /> Add a row
            </button>
            {drafts.some(hoursDraftComplete) && (
              <span className="text-12 text-ink-muted tabular-nums">
                Total {centiToHoursText(totalCenti(drafts))} h ({totalCenti(drafts)} centi-hours)
              </span>
            )}
          </div>
          {/* A single row with no labels IS the s.8(2)(e)(i) aggregate;
              several labelled rows ARE the (ii) breakdown. No mode flag —
              the shape of the rows is the presentation. */}
          <div className="text-11 text-ink-muted leading-relaxed">
            One row with no labels records a single total. Several rows with a work type or rate
            record the breakdown — both satisfy s.8(2)(e), and the choice is yours.
          </div>
        </div>
      )}
    </div>
  )
}
